import type { ProjectDashboardData } from '../utils/projectDataLoader'
import type { Permit } from '@/types/permits'
import { api } from '@/lib/axios-client'
import type {
  Activity,
  DocumentDetails,
  documentListItem,
  ProjectKPIs,
  ProjectStatistics,
} from '../types/dashboard-permit-tracking-props'
import type { ProjectProgressPayload } from '@/features/city-clerk/shared/types/job-types'

const documentsData: documentListItem[] = [
  {
    id: '1',
    documentName: 'Employee Handbook',
    issues: 2,
    issueTypes: 'error',
    currentVersion: 'v1.3',
    lastUpdated: '2024-11-12',
  },
  {
    id: '2',
    documentName: 'Privacy Policy',
    issues: 0,
    issueTypes: 'none',
    currentVersion: 'v2.1',
    lastUpdated: '2024-10-05',
  },
  {
    id: '3',
    documentName: 'Code of Conduct',
    issues: 1,
    issueTypes: 'warning',
    currentVersion: 'v1.0',
    lastUpdated: '2024-12-01',
  },
  {
    id: '4',
    documentName: 'Security Guidelines',
    issues: 4,
    issueTypes: 'affected',
    currentVersion: 'v3.2',
    lastUpdated: '2024-09-18',
  },
]
export const documentDetailsData: DocumentDetails[] = [
  {
    documentId: '1',
    comments: [
      {
        field: 'Title',
        commenter: 'Alice Johnson',
        date: '2025-01-05',
        commentText: 'The title contains a typo.',
        type: 'error',
      },
      {
        field: 'Summary',
        commenter: 'Bob Smith',
        date: '2025-01-06',
        commentText: 'Consider clarifying this section.',
        type: 'warning',
      },
      {
        field: 'Conclusion',
        commenter: 'Carol Lee',
        date: '2025-01-07',
        commentText: 'This section is affected by recent changes.',
        type: 'affected',
      },
    ],
    versions: [
      {
        versionNumber: '1.0',
        isCurrent: false,
        updatedOn: '2025-01-01',
        size: '1.2 MB',
        issues: ['Missing references', 'Typo in introduction'],
        improvements: ['Initial draft created'],
        aiConfidenceScore: 72,
      },
      {
        versionNumber: '1.1',
        isCurrent: true,
        updatedOn: '2025-01-04',
        size: '1.3 MB',
        issues: ['Formatting inconsistency'],
        improvements: ['Fixed typos', 'Updated references'],
        aiConfidenceScore: 80,
      },
    ],
  },
  {
    documentId: '2',
    comments: [
      {
        field: 'Abstract',
        commenter: 'David Brown',
        date: '2025-01-08',
        commentText: 'Abstract is too long.',
        type: 'warning',
      },
      {
        field: 'Methodology',
        commenter: 'Emma Wilson',
        date: '2025-01-09',
        commentText: 'Incorrect formula used in step 2.',
        type: 'error',
      },
    ],
    versions: [
      {
        versionNumber: 'v2.0',
        isCurrent: false,
        updatedOn: '2025-01-03',
        size: '2.0 MB',
        issues: ['Methodology needs revision'],
        improvements: ['Major content update'],
        aiConfidenceScore: 65,
      },
      {
        versionNumber: 'v2.1',
        isCurrent: true,
        updatedOn: '2025-01-10',
        size: '2.2 MB',
        improvements: ['Corrected formulas', 'Improved clarity'],
        aiConfidenceScore: 85,
      },
    ],
  },
]

export const projectsDataMap: Record<string, ProjectDashboardData> = {}

export function getProjectData(projectId: string): ProjectDashboardData | null {
  return projectsDataMap[projectId] || null
}

export async function getAllPermitsByCityId(
  cityId: string,
): Promise<Permit[] | null> {
  const allPermits = await api.get<Permit[]>(`/Permit/CityId?cityId=${cityId}`)
  if (!allPermits) return null
  return allPermits
}

export const getPermitsByProjectId = async function (
  projectId: string,
): Promise<Permit[] | null> {
  const permits = await api.get<Permit[]>(
    `/Permit/ProjectId?projectId=${projectId}`,
  )
  if (!permits) return null
  return permits
}

export const getPermitsById = async function (
  permitId: string,
): Promise<Permit | null> {
  const permit = await api.get<Permit>(`/Permit/Id?permitId=${permitId}`)
  if (!permit) return null
  return permit
}

export function getDocumentsByPermitId(permitId: string): documentListItem[] {
  return permitId ? documentsData : []
}

export function getDocumentDetailsByDocumentId(
  documentId: string,
): DocumentDetails | null {
  const details = documentDetailsData.find(
    (doc) => doc.documentId === documentId,
  )
  return details || null
}

export async function getProjectStatistics(
  projectId: string,
): Promise<ProjectStatistics> {
  const res = await api.get<ProjectStatistics>(
    `/Project/Statistics?projectId=${projectId}`,
  )
  return res
}

export async function getProjectKPIs(projectId: string): Promise<ProjectKPIs> {
  const res = await api.get<ProjectKPIs>(`/Project/KPIs?projectId=${projectId}`)

  return res
}

export async function getProjectActivities(
  projectId: string,
  count: number,
): Promise<Activity[]> {
  const res = await api.get<Activity[]>(
    `/Project/ActivePermits?projectId=${projectId}&count=${count}`,
  )
  return res
}

export async function getProjectProgress(
  projectId: string,
): Promise<ProjectProgressPayload | null> {
  try {
    const res = await api.get<ProjectProgressPayload>(
      `/check/project/${projectId}/progress`,
    )
    return res ?? null
  } catch {
    return null
  }
}

// User-initiated "stop processing" for a permit. Cancels every in-flight
// check tied to submittalId — LOCAL ONLY, see the backend's
// ICheckCompletionService.CancelChecksBySubmittalAsync: this stops QPoint
// from waiting on the result, it does not abort work already in flight on
// qc_api/cv-core. Returns the number of steps actually cancelled.
export async function cancelChecksBySubmittal(
  submittalId: string,
  reason?: string,
): Promise<number> {
  const res = await api.post<{ cancelledStepCount: number }>(
    `/Check/submittal/${submittalId}/cancel`,
    reason ? { reason } : undefined,
  )
  return res.cancelledStepCount
}
